import { SleepDomainService } from './domain';
import { SleepFactory } from './entity/sleepFactory';

import type { SleepEntity } from './entity/sleepEntity';
import type { SleepIRepository } from './entity/sleepIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { SleepJson, SleepJsonPvt } from '@/types/domain/sleep/entities/sleepEntity';

import { repository } from '@/share';

/**
 * 睡眠アプリケーションサービス
 */
export class SleepApplicationService {
  private service!: SleepDomainService;

  private sleepFactory = new SleepFactory();

  /**
   * Injection
   * @param sleepRepository sleepRepository
   * @param logger logger
   */
  public inject(
    sleepRepository: SleepIRepository,
    logger: Logger,
  ) {
    this.service = new SleepDomainService(logger);
    this.service.inject(sleepRepository);
  }

  /**
   * 睡眠取得
   * @param token トークン
   * @returns 一覧
   */
  async getSleepList(token: string | null) {
    const result = await repository.sleep.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: SleepEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * 指定日の睡眠リスト取得
   * @param date 日付
   * @returns リスト
   */
  async getSleepListWithDate(date: string) {
    const result = await this.service.getSleepListWithDate(date);
    return result;
  }

  /**
   * 睡眠作成
   * @param input 入力
   * @returns 結果
   */
  async createSleep(
    input: SleepJson,
  ) {
    const entity = this.sleepFactory.createFromJson(input);
    const result = await repository.sleep.create(entity);
    return result;
  }

  /**
   * 睡眠更新
   * @param input 入力
   * @returns 結果
   */
  async updateSleep(
    input: SleepJson,
  ) {
    const entity = this.sleepFactory.createFromJson(input);
    const result = await repository.sleep.update(entity);
    return result;
  }

  /**
   * 睡眠削除
   * @param input 入力
   * @returns 結果
   */
  async deleteSleep(
    input: SleepJson,
  ) {
    const entity = this.sleepFactory.createFromJson(input);
    const result = await repository.sleep.delete(entity);
    return result;
  }

  /**
   * 睡眠リストを更新する(Create)
   * @param sleepList 睡眠リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createSleepStore(
    sleepList: SleepJson[],
    payload: EntitySubscribeType,
    filter: ((json: SleepJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      sleepList,
      payload,
      this.sleepFactory,
      filter,
    );
  }

  /**
   * 睡眠リストを更新する(Update)
   * @param sleepList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateSleepStore(
    sleepList: SleepJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      sleepList,
      payload,
      this.sleepFactory,
    );
  }

  /**
   * 睡眠リストを更新する(Delete)
   * @param sleepList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteSleepStore(
    sleepList: SleepJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      sleepList,
      payload,
      this.sleepFactory,
    );
  }
}
